import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Share2,
  Settings,
  Users,
  Activity,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Shield,
  BarChart3,
  Clock,
  AlertTriangle,
  Send,
  TrendingUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBoberdooSettingsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [globalEnabled, setGlobalEnabled] = useState(false);
  const [clientConfigs, setClientConfigs] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({
    configuredClients: 0,
    activeClients: 0,
    totalEvents: 0,
    leadsPosted: 0,
    successfulPosts: 0,
    failedPosts: 0
  });
  const [eventFilters, setEventFilters] = useState({
    client_id: '',
    event_type: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dme_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch global settings
      const settingsRes = await axios.get(`${API_URL}/api/admin/settings/boberdoo`, { headers });
      setGlobalEnabled(settingsRes.data.enabled || false);
      
      const statsData = settingsRes.data.stats || {};
      setStats({
        configuredClients: statsData.configured_clients || 0,
        activeClients: statsData.active_clients || 0,
        totalEvents: statsData.total_events || 0,
        leadsPosted: statsData.total_leads_posted || 0,
        successfulPosts: statsData.successful_posts || 0,
        failedPosts: statsData.failed_posts || 0
      });

      // Fetch all client configs
      const configsRes = await axios.get(`${API_URL}/api/admin/boberdoo/configs`, { headers });
      setClientConfigs(configsRes.data.configs || []);

      // Fetch ALL clients
      const clientsRes = await axios.get(`${API_URL}/api/admin/clients`, { headers });
      setAllClients(clientsRes.data || []);

      // Fetch recent events
      const eventsRes = await axios.get(`${API_URL}/api/admin/boberdoo/events`, { 
        headers,
        params: { limit: 50 }
      });
      setEvents(eventsRes.data.events || []);

    } catch (error) {
      console.error('Error fetching Boberdoo settings:', error);
      toast.error('Failed to load Boberdoo settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleGlobal = async (enabled) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('dme_token');
      await axios.put(
        `${API_URL}/api/admin/settings/boberdoo`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGlobalEnabled(enabled);
      toast.success(`Boberdoo ${enabled ? 'enabled' : 'disabled'} globally`);
    } catch (error) {
      toast.error('Failed to update Boberdoo setting');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleClient = async (clientId, currentEnabled) => {
    const newEnabled = !currentEnabled;
    try {
      const token = localStorage.getItem('dme_token');
      await axios.put(
        `${API_URL}/api/admin/clients/${clientId}/boberdoo/toggle`,
        { enabled: newEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setAllClients(prev => prev.map(c => 
        c.id === clientId ? { ...c, boberdoo_enabled: newEnabled } : c
      ));
      
      toast.success(`Boberdoo ${newEnabled ? 'enabled' : 'disabled'} for client`);
    } catch (error) {
      toast.error('Failed to update client Boberdoo setting');
    }
  };

  const handleFilterEvents = async () => {
    try {
      const token = localStorage.getItem('dme_token');
      const params = { limit: 50 };
      if (eventFilters.client_id) params.client_id = eventFilters.client_id;
      if (eventFilters.event_type) params.event_type = eventFilters.event_type;
      
      const res = await axios.get(`${API_URL}/api/admin/boberdoo/events`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setEvents(res.data.events || []);
    } catch (error) {
      toast.error('Failed to filter events');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-boberdoo-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Share2 className="w-6 h-6 text-teal-600" />
              Boberdoo Settings
            </h1>
            <p className="text-muted-foreground">Manage Boberdoo lead distribution integration for clients</p>
          </div>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.configuredClients}</p>
                <p className="text-sm text-muted-foreground">Configured Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeClients}</p>
                <p className="text-sm text-muted-foreground">Active Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Send className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.leadsPosted}</p>
                <p className="text-sm text-muted-foreground">Leads Posted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {stats.leadsPosted > 0 
                    ? Math.round((stats.successfulPosts / stats.leadsPosted) * 100) 
                    : 0}%
                </p>
                <p className="text-sm text-muted-foreground">Success Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Global Toggle */}
      <Card>
        <CardHeader>
          <CardTitle>Global Boberdoo Setting</CardTitle>
          <CardDescription>Enable or disable Boberdoo for all clients platform-wide</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className={`w-8 h-8 ${globalEnabled ? 'text-green-600' : 'text-gray-400'}`} />
              <div>
                <p className="font-medium">Boberdoo Lead Distribution</p>
                <p className="text-sm text-muted-foreground">
                  {globalEnabled ? 'Boberdoo integration is active' : 'Boberdoo is disabled platform-wide'}
                </p>
              </div>
            </div>
            <Switch
              checked={globalEnabled}
              onCheckedChange={handleToggleGlobal}
              disabled={saving}
              data-testid="boberdoo-global-toggle"
            />
          </div>
          
          {globalEnabled && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">Boberdoo is Active</p>
                  <p className="text-sm text-green-700">
                    Clients with Boberdoo enabled can configure their API credentials and post leads to Boberdoo.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Access Control */}
      {globalEnabled && (
        <Card>
          <CardHeader>
            <CardTitle>Client Access</CardTitle>
            <CardDescription>Toggle Boberdoo access for each client</CardDescription>
          </CardHeader>
          <CardContent>
            {allClients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No clients found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allClients.map((client) => (
                  <div 
                    key={client.id} 
                    className={`flex items-center justify-between p-3 border rounded-lg transition-colors ${
                      client.boberdoo_enabled ? 'bg-green-50 border-green-200' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${client.boberdoo_enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <div>
                        <p className="font-medium">{client.company_name || client.email}</p>
                        <p className="text-sm text-muted-foreground">{client.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm ${client.boberdoo_enabled ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                        {client.boberdoo_enabled ? 'ON' : 'OFF'}
                      </span>
                      <Switch
                        checked={client.boberdoo_enabled || false}
                        onCheckedChange={() => handleToggleClient(client.id, client.boberdoo_enabled)}
                        data-testid={`client-toggle-${client.id}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Client Configurations */}
      <Card>
        <CardHeader>
          <CardTitle>Client Boberdoo Configurations</CardTitle>
          <CardDescription>Manage Boberdoo settings for individual clients</CardDescription>
        </CardHeader>
        <CardContent>
          {clientConfigs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Share2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No clients have configured Boberdoo yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Source ID</TableHead>
                  <TableHead>Post Mode</TableHead>
                  <TableHead>Leads Posted</TableHead>
                  <TableHead>Success Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Test</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientConfigs.map((config) => {
                  const successRate = config.total_leads_posted > 0 
                    ? Math.round((config.successful_posts / config.total_leads_posted) * 100) 
                    : 0;
                  
                  return (
                    <TableRow key={config.client_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{config.client_name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{config.client_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                          {config.source_id || 'Not set'}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {config.post_mode === 'ping_post' ? 'Ping Post' : 'Direct'}
                        </Badge>
                      </TableCell>
                      <TableCell>{config.total_leads_posted || 0}</TableCell>
                      <TableCell>
                        <span className={successRate >= 90 ? 'text-green-600' : successRate >= 70 ? 'text-yellow-600' : 'text-red-600'}>
                          {successRate}%
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={config.is_active ? 'default' : 'secondary'}>
                          {config.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {config.last_test_at ? (
                          <div className="flex items-center gap-1">
                            {config.last_test_result === 'success' ? (
                              <CheckCircle className="w-3 h-3 text-green-600" />
                            ) : (
                              <XCircle className="w-3 h-3 text-red-600" />
                            )}
                            {formatDate(config.last_test_at)}
                          </div>
                        ) : (
                          'Never'
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={config.is_active}
                          onCheckedChange={(checked) => handleToggleClient(config.client_id, checked)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Events Log */}
      {globalEnabled && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Boberdoo Events Log</CardTitle>
                <CardDescription>Recent Boberdoo-related events and activities</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select 
                  value={eventFilters.event_type} 
                  onValueChange={(v) => setEventFilters(prev => ({ ...prev, event_type: v }))}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Event Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Events</SelectItem>
                    <SelectItem value="config_saved">Config Saved</SelectItem>
                    <SelectItem value="connection_test">Connection Test</SelectItem>
                    <SelectItem value="lead_posted">Lead Posted</SelectItem>
                    <SelectItem value="lead_post_failed">Post Failed</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={handleFilterEvents}>
                  Filter
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No events recorded yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.slice(0, 20).map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(event.timestamp)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs">{event.client_id?.slice(0, 8)}...</code>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={
                            event.event_type === 'lead_posted' ? 'default' :
                            event.event_type === 'lead_post_failed' ? 'destructive' :
                            'outline'
                          }
                        >
                          {event.event_type?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {event.details?.success !== undefined && (
                          <span className={event.details.success ? 'text-green-600' : 'text-red-600'}>
                            {event.details.success ? 'Success' : 'Failed'}
                          </span>
                        )}
                        {event.details?.confirmation_id && (
                          <span className="ml-2 text-muted-foreground">
                            Lead: {event.details.confirmation_id}
                          </span>
                        )}
                        {event.details?.error && (
                          <span className="text-red-600"> - {event.details.error}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            About Boberdoo Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Boberdoo is a lead distribution platform that allows you to post leads to their marketplace.
            When enabled for a client, leads assigned to them can be automatically posted to Boberdoo
            via the Ping Post API.
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Clients must have Boberdoo enabled by an admin before they can configure it</li>
            <li>Each client needs their own Boberdoo API key and source ID</li>
            <li>Supports both Ping Post and Direct Post modes</li>
            <li>All lead posts are logged for audit purposes</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
